import { SAVE_KEYS } from "../../config/gameplay";
import { createFreshSave } from "./defaults";
import { chooseBestSave, progressScore, sanitizeSave } from "./sanitize";
import type { LoadResult, OrbitForgeSave, SaveWriteResult } from "./types";

export function loadOrbitForgeSave(storage: Storage | undefined, nowUnixSeconds: number): LoadResult {
  if (!storage) {
    return { save: createFreshSave(nowUnixSeconds), source: "fresh" };
  }

  const primary = readSave(storage, SAVE_KEYS.primary, nowUnixSeconds);
  const backup = readSave(storage, SAVE_KEYS.backup, nowUnixSeconds);

  if (!primary && !backup) {
    return { save: createFreshSave(nowUnixSeconds), source: "fresh" };
  }

  if (primary && !backup) {
    return { save: primary, source: "primary" };
  }

  if (!primary && backup) {
    return { save: backup, source: "backup" };
  }

  if (primary && backup) {
    const selected = chooseBestSave(primary, backup);
    return { save: selected, source: selected === primary ? "primary" : "backup" };
  }

  return { save: createFreshSave(nowUnixSeconds), source: "fresh" };
}

export function writeOrbitForgeSave(storage: Storage | undefined, save: OrbitForgeSave, allowProgressLoss = false): SaveWriteResult {
  if (!storage) {
    return { ok: false, reason: "storage-unavailable" };
  }

  const nextScore = progressScore(save);
  if (!allowProgressLoss && save.previousProgressScore > 0 && nextScore + 1 < save.previousProgressScore * 0.72) {
    return { ok: false, reason: "suspicious-progress-loss" };
  }

  const updatedSave: OrbitForgeSave = {
    ...save,
    previousProgressScore: Math.max(save.previousProgressScore, nextScore)
  };
  const serialized = JSON.stringify(updatedSave);
  storage.setItem(SAVE_KEYS.primary, serialized);
  storage.setItem(SAVE_KEYS.backup, serialized);
  return { ok: true };
}

function readSave(storage: Storage, key: string, nowUnixSeconds: number): OrbitForgeSave | null {
  const raw = storage.getItem(key);
  if (!raw) {
    return null;
  }

  try {
    return sanitizeSave(JSON.parse(raw), nowUnixSeconds);
  } catch {
    return null;
  }
}
